import { useMemo, useState } from 'react';

import { formatJalaliDate } from '@/lib/format';
import { formatNumber } from '@/features/reports/lib/format';
import { fa, renderMessage } from '../i18n/fa';

const PAGE_SIZE = 20;
const UNLINKED_CHAIN = '__unlinked__';
const SEVERITY_ORDER = Object.keys(fa.severity);
const COVERAGE_RULE = Object.keys(fa.rules).find((rule) => fa.ruleName(rule) === fa.chain.coverage);

const csvCell = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
const getMessage = (alert, units) => renderMessage(alert.message_code, alert.message_params, units);

function compareAlerts(left, right, sort) {
  const dateOrder = String(right.alert.date ?? '').localeCompare(String(left.alert.date ?? ''));
  if (sort === 'date') return dateOrder || left.index - right.index;
  if (sort === 'rule') {
    return fa.ruleName(left.alert.rule).localeCompare(fa.ruleName(right.alert.rule), 'fa')
      || left.alert.rule.localeCompare(right.alert.rule)
      || dateOrder;
  }
  const severityRank = (severity) => {
    const index = SEVERITY_ORDER.indexOf(severity);
    return index < 0 ? SEVERITY_ORDER.length : index;
  };
  const severityOrder = severityRank(left.alert.severity) - severityRank(right.alert.severity);
  return severityOrder || dateOrder || left.index - right.index;
}

function createCsv(alerts, commodity, units) {
  const includeEvidence = alerts.some(({ evidence }) => evidence != null);
  const headers = [
    fa.charts.alerts.date,
    fa.charts.alerts.severity,
    fa.charts.alerts.rule,
    fa.charts.alerts.commodity,
    fa.charts.alerts.chainId,
    fa.charts.alerts.message,
  ];
  if (includeEvidence) headers.push(fa.charts.alerts.evidence);

  const rows = alerts.map((alert) => {
    const row = [
      formatJalaliDate(alert.date),
      fa.severityLabel(alert.severity),
      fa.charts.alerts.exportRule(fa.ruleName(alert.rule), alert.rule),
      fa.commodityLabel(commodity),
      alert.chain_id ?? fa.charts.alerts.unlinked,
      getMessage(alert, units),
    ];
    if (includeEvidence) row.push(alert.evidence == null ? '' : JSON.stringify(alert.evidence));
    return row;
  });

  return [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}

export default function AlertsPanel({
  alerts = [],
  chains = [],
  commodity,
  onSelect,
  units,
  filters,
  onFilterChange,
  onClearFilters,
}) {
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const severityOptions = useMemo(
    () => [...new Set(alerts.map((alert) => alert.severity).filter(Boolean))]
      .sort((left, right) => {
        const leftIndex = SEVERITY_ORDER.indexOf(left);
        const rightIndex = SEVERITY_ORDER.indexOf(right);
        return (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex)
          - (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex);
      }),
    [alerts],
  );
  const ruleOptions = useMemo(
    () => [...new Set(alerts.map((alert) => alert.rule).filter(Boolean))]
      .sort((left, right) => fa.ruleName(left).localeCompare(fa.ruleName(right), 'fa')),
    [alerts],
  );
  const chainOptions = useMemo(
    () => [...new Set([
      ...chains.map((chain) => chain.id),
      ...alerts.map((alert) => alert.chain_id),
    ].filter(Boolean))].sort((left, right) => left.localeCompare(right)),
    [alerts, chains],
  );
  const severityCounts = useMemo(() => {
    const counts = Object.fromEntries(severityOptions.map((severity) => [severity, 0]));
    alerts.forEach((alert) => {
      const matchesRule = !filters.rule || alert.rule === filters.rule;
      const matchesChain = !filters.chain
        || (filters.chain === UNLINKED_CHAIN
          ? !alert.chain_id
          : alert.chain_id === filters.chain
            || (!chainOptions.includes(filters.chain) && alert.chain_id?.includes(filters.chain)));
      const matchesSearch = !filters.search
        || `${getMessage(alert, units)} ${alert.message ?? ''}`
          .toLocaleLowerCase()
          .includes(filters.search.toLocaleLowerCase());
      if (matchesRule && matchesChain && matchesSearch) counts[alert.severity] += 1;
    });
    return counts;
  }, [alerts, chainOptions, filters.chain, filters.rule, filters.search, severityOptions, units]);

  const filteredAlerts = useMemo(() => alerts
    .map((alert, index) => ({ alert, index }))
    .filter(({ alert }) => (
      (!filters.severities.length || filters.severities.includes(alert.severity))
      && (!filters.rule || alert.rule === filters.rule)
      && (!filters.chain
        || (filters.chain === UNLINKED_CHAIN
          ? !alert.chain_id
          : alert.chain_id === filters.chain
            || (!chainOptions.includes(filters.chain) && alert.chain_id?.includes(filters.chain))))
      && (!filters.search
        || `${getMessage(alert, units)} ${alert.message ?? ''}`
          .toLocaleLowerCase()
          .includes(filters.search.toLocaleLowerCase()))
    ))
    .sort((left, right) => compareAlerts(left, right, filters.sort)),
  [alerts, chainOptions, filters.chain, filters.rule, filters.search, filters.severities, filters.sort, units]);

  const displayItems = useMemo(() => {
    const lowestSeverity = SEVERITY_ORDER.at(-1);
    const groups = new Map();
    filteredAlerts.forEach((entry) => {
      const { alert } = entry;
      if (alert.severity !== lowestSeverity) return;
      const group = groups.get(alert.rule) ?? [];
      group.push(entry);
      groups.set(alert.rule, group);
    });

    const emitted = new Set();
    return filteredAlerts.flatMap((entry) => {
      const { alert } = entry;
      const group = alert.severity === lowestSeverity ? groups.get(alert.rule) : null;
      if (!group || group.length < 2) return [{ type: 'alert', ...entry }];
      if (emitted.has(alert.rule)) return [];
      emitted.add(alert.rule);
      return [{ type: 'group', key: `group:${alert.rule}`, rule: alert.rule, entries: group }];
    });
  }, [filteredAlerts]);

  const pageCount = Math.max(1, Math.ceil(displayItems.length / PAGE_SIZE));
  const currentPage = Math.min(filters.page, pageCount);
  const visibleItems = displayItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const generatedAt = formatJalaliDate(new Date());
  const activeFilterLabels = [
    ...filters.severities.map((severity) => fa.charts.alerts.severityFilter(fa.severityLabel(severity))),
    ...(filters.rule ? [fa.charts.alerts.ruleFilter(`${fa.ruleName(filters.rule)} (${filters.rule})`)] : []),
    ...(filters.chain ? [fa.charts.alerts.chainFilter(
      filters.chain === UNLINKED_CHAIN ? fa.charts.alerts.unlinked : filters.chain,
    )] : []),
    ...(filters.search ? [fa.charts.alerts.searchFilter(filters.search)] : []),
    ...(filters.sort !== 'severity'
      ? [fa.charts.alerts.sortFilter(fa.charts.alerts[`sort${filters.sort[0].toUpperCase()}${filters.sort.slice(1)}`])]
      : []),
  ];

  const toggleSeverity = (severity) => {
    const next = filters.severities.includes(severity)
      ? filters.severities.filter((selected) => selected !== severity)
      : [...filters.severities, severity];
    onFilterChange({ severities: next });
  };

  const handleExport = () => {
    if (!filteredAlerts.length) return;
    const csv = createCsv(filteredAlerts.map(({ alert }) => alert), commodity, units);
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `traceability-alerts-${commodity}-${generatedAt.replaceAll('/', '-')}.csv`;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const renderAlert = ({ alert, index }) => (
    <button
      key={`${alert.rule}-${alert.chain_id ?? 'unlinked'}-${alert.date}-${index}`}
      type="button"
      className="tr-alert"
      data-severity={alert.severity}
      onClick={() => alert.chain_id && onSelect(alert.chain_id)}
      disabled={!alert.chain_id}
    >
      <span className={`tr-pill tr-pill-${alert.severity}`}>{fa.severityLabel(alert.severity)}</span>
      <span className="tr-alert-body">
        <span className="tr-alert-head">
          <b>{fa.ruleName(alert.rule)}</b>
          <span className="tr-alert-chain" dir="ltr">
            {alert.chain_id ?? fa.charts.alerts.unlinked}
          </span>
          <time dateTime={alert.date}>{formatJalaliDate(alert.date)}</time>
        </span>
        <span className="tr-sub">{getMessage(alert, units)}</span>
      </span>
    </button>
  );

  return (
    <div className="tr-card tr-panel">
      <h2 className="tr-title">
        {fa.charts.alerts.title}
        <span className="tr-hint">{fa.charts.alerts.hint}</span>
      </h2>

      <div className="tr-alert-toolbar">
        <div className="tr-alert-filter-field tr-alert-severities" aria-label={fa.charts.alerts.severity}>
          {severityOptions.map((severity) => (
            <button
              key={severity}
              type="button"
              className={`tr-alert-chip tr-pill-${severity}`}
              aria-pressed={!filters.severities.length || filters.severities.includes(severity)}
              onClick={() => toggleSeverity(severity)}
            >
              {fa.severityLabel(severity)}
              <span>{formatNumber(severityCounts[severity] ?? 0)}</span>
            </button>
          ))}
          <span className="tr-alert-filter-hint">{fa.charts.alerts.allSeverities}</span>
        </div>

        <label className="tr-alert-filter-field">
          <span>{fa.charts.alerts.rule}</span>
          <select
            value={filters.rule}
            onChange={(event) => onFilterChange({ rule: event.target.value })}
          >
            <option value="">{fa.charts.alerts.allRules}</option>
            {!ruleOptions.includes(filters.rule) && filters.rule && (
              <option value={filters.rule}>{fa.ruleName(filters.rule)} ({filters.rule})</option>
            )}
            {ruleOptions.map((rule) => (
              <option key={rule} value={rule}>{fa.ruleName(rule)} ({rule})</option>
            ))}
          </select>
        </label>

        <label className="tr-alert-filter-field">
          <span>{fa.charts.alerts.chain}</span>
          <input
            type="search"
            list="traceability-alert-chains"
            value={filters.chain === UNLINKED_CHAIN ? fa.charts.alerts.unlinked : filters.chain}
            placeholder={fa.charts.alerts.allChains}
            onChange={(event) => onFilterChange({
              chain: event.target.value === fa.charts.alerts.unlinked
                ? UNLINKED_CHAIN
                : event.target.value,
            })}
          />
          <datalist id="traceability-alert-chains">
            {chainOptions.map((chainId) => <option key={chainId} value={chainId} />)}
            {alerts.some((alert) => !alert.chain_id) && (
              <option value={fa.charts.alerts.unlinked} />
            )}
          </datalist>
        </label>

        <label className="tr-alert-filter-field">
          <span>{fa.charts.alerts.sort}</span>
          <select value={filters.sort} onChange={(event) => onFilterChange({ sort: event.target.value })}>
            <option value="severity">{fa.charts.alerts.sortSeverity}</option>
            <option value="date">{fa.charts.alerts.sortDate}</option>
            <option value="rule">{fa.charts.alerts.sortRule}</option>
          </select>
        </label>

        <label className="tr-alert-filter-field tr-alert-search">
          <span>{fa.charts.alerts.search}</span>
          <input
            type="search"
            value={filters.search}
            onChange={(event) => onFilterChange({ search: event.target.value })}
          />
        </label>
      </div>

      <div className="tr-alert-actions">
        <span className="tr-alert-count" aria-live="polite">
          {fa.charts.alerts.resultCount(filteredAlerts.length)}
        </span>
        <div>
          <button type="button" onClick={onClearFilters}>{fa.charts.alerts.clear}</button>
          <button type="button" onClick={handleExport} disabled={!filteredAlerts.length}>
            {fa.charts.alerts.exportCsv}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            disabled={!filteredAlerts.length}
          >
            {fa.charts.alerts.print}
          </button>
        </div>
      </div>

      {!filteredAlerts.length && (
        <p className="tr-sub tr-empty-inline">{filters.severities.length
          || filters.rule || filters.chain || filters.search
          ? fa.charts.alerts.noResults
          : fa.charts.alerts.empty}
        </p>
      )}

      {visibleItems.map((item) => {
        if (item.type === 'alert') return renderAlert(item);
        const isExpanded = expandedGroups.has(item.key);
        const chainCount = new Set(item.entries.map(({ alert }) => alert.chain_id).filter(Boolean)).size;
        const summary = item.rule === COVERAGE_RULE
          ? fa.charts.alerts.groupCoverage(chainCount)
          : chainCount
            ? fa.charts.alerts.groupRule(fa.ruleName(item.rule), chainCount)
            : fa.charts.alerts.groupUnlinked(item.entries.length);
        return (
          <div className="tr-alert-group" key={item.key}>
            <button
              type="button"
              className="tr-alert tr-alert-group-toggle"
              data-severity={item.entries[0].alert.severity}
              aria-expanded={isExpanded}
              onClick={() => setExpandedGroups((current) => {
                const next = new Set(current);
                if (next.has(item.key)) next.delete(item.key);
                else next.add(item.key);
                return next;
              })}
            >
              <span className={`tr-pill tr-pill-${item.entries[0].alert.severity}`}>
                {fa.severityLabel(item.entries[0].alert.severity)}
              </span>
              <span className="tr-alert-body">
                <span className="tr-alert-head">
                  <b>{fa.ruleName(item.rule)}</b>
                  <span>{summary}</span>
                  <span className="tr-alert-chain">
                    {fa.charts.alerts.groupToggle(isExpanded)}
                  </span>
                </span>
              </span>
            </button>
            {isExpanded && (
              <div className="tr-alert-group-items">
                {item.entries.map(renderAlert)}
              </div>
            )}
          </div>
        );
      })}

      {pageCount > 1 && (
        <nav className="tr-alert-pagination" aria-label={fa.charts.alerts.title}>
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => onFilterChange({ page: currentPage - 1 }, { resetPage: false })}
          >
            {fa.charts.alerts.previousPage}
          </button>
          <span>{fa.charts.alerts.page(currentPage, pageCount)}</span>
          <button
            type="button"
            disabled={currentPage >= pageCount}
            onClick={() => onFilterChange({ page: currentPage + 1 }, { resetPage: false })}
          >
            {fa.charts.alerts.nextPage}
          </button>
        </nav>
      )}

      <section className="tr-print-report" aria-label={fa.charts.alerts.title}>
        <h1>{fa.charts.alerts.title} · {fa.commodityLabel(commodity)}</h1>
        <p>{fa.charts.alerts.generatedAt(generatedAt)}</p>
        <h2>{fa.charts.alerts.activeFilters}</h2>
        <p>{activeFilterLabels.length
          ? activeFilterLabels.join(' · ')
          : fa.charts.alerts.allSeverities}
        </p>
        <p>{fa.charts.alerts.resultCount(filteredAlerts.length)}</p>
        <table>
          <thead>
            <tr>
              <th>{fa.charts.alerts.date}</th>
              <th>{fa.charts.alerts.severity}</th>
              <th>{fa.charts.alerts.rule}</th>
              <th>{fa.charts.alerts.commodity}</th>
              <th>{fa.charts.alerts.chainId}</th>
              <th>{fa.charts.alerts.message}</th>
            </tr>
          </thead>
          <tbody>
            {filteredAlerts.map(({ alert, index }) => (
              <tr key={`print-${index}`}>
                <td>{formatJalaliDate(alert.date)}</td>
                <td>{fa.severityLabel(alert.severity)}</td>
                <td>{fa.charts.alerts.exportRule(fa.ruleName(alert.rule), alert.rule)}</td>
                <td>{fa.commodityLabel(commodity)}</td>
                <td dir="ltr">{alert.chain_id ?? fa.charts.alerts.unlinked}</td>
                <td>{getMessage(alert, units)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
